<template>
  <PageHeader
    breadcrumb
    :items="[
      { label: $t('ui.entities.setting', 2), to: '/settings' },
      { label: 'Cron Jobs' }
    ]"
  ></PageHeader>

  <Card>
    <DataTable
      ref="dt"
      v-model:filters="filters"
      :value="cronjobs"
      removable-sort
      sort-field="id"
      :rows="20"
      data-key="id"
      column-resize-mode="fit"
      :paginator="true"
      responsive-layout="scroll"
      filter-display="menu"
      :rows-per-page-options="[10, 20, 50]"
      state-key="dt-settings-admin-cronjobs"
      state-storage="session"
      current-page-report-template="Showing {startRecord}-{endRecord} out of {totalRecords}"
      :global-filter-fields="['id', 'name', 'slug', 'cronExpression', 'scriptPath']"
      :loading="cronjobsApiRequest.isLoading.value"
    >
      <template #empty>No records found</template>
      <template #header>
        <div class="flex justify-between">
          <span class="flex justify-between gap-4">
            <span>
              <Input
                v-model="filters['global'].value"
                :placeholder="$t('ui.common.search', 1)"
                icon="search"
              />
            </span>
            <Button
              :label="$t('ui.actions.clear', 1)"
              severity="secondary"
              type="button"
              outlined
              icon="filter-slash"
              @click="clearFilters()"
            />
          </span>
        </div>
        <!-- <div class="flex justify-between mt-4">
          <span>
            <Button label="CSV" type="button" severity="secondary" outlined icon="file-excel" @click="exportCSV($event)" />
          </span>
        </div> -->
      </template>

      <Column
        field="id"
        header="ID"
        :sortable="true"
      >
        <template #body="{ data }">
          {{ data.id }}
        </template>
      </Column>
      <Column
        field="name"
        header="Name"
        :sortable="true"
      >
        <template #body="{ data }">
          {{ data.name }}
        </template>
      </Column>
      <Column
        field="cronExpression"
        header="Cron Expression"
        :sortable="true"
      >
        <template #body="{ data }">
          <span>{{ data.cronExpression }}</span>
        </template>
      </Column>
      <Column
        field="scriptPath"
        header="Script"
        :sortable="true"
      >
        <template #body="{ data }">
          <span
            v-tippy="data.scriptPath"
            class="block max-w-[360px] truncate font-mono text-xs"
          >
            {{ data.scriptPath }}
          </span>
        </template>
      </Column>
      <Column
        field="lastFinishedAt"
        header="Last Run"
        :sortable="true"
      >
        <template #body="{ data }">
          <span v-if="data.lastFinishedAt" v-tippy="dayjs(data.lastFinishedAt).format('lll')">
            {{ dayjs(data.lastFinishedAt).from(dayjs()) }}
          </span>
          <span v-else>-</span>
        </template>
      </Column>
      <Column
        field="lastErrorAt"
        header="Last Error At"
        :sortable="true"
      >
        <template #body="{ data }">
          <span v-if="data.lastErrorAt">
            <span v-tippy="dayjs(data.lastErrorAt).format('lll')">{{ dayjs(data.lastErrorAt).from(dayjs()) }}</span>
          </span>
          <span v-else>-</span>
        </template>
      </Column>
      <Column
        field="isActive"
        header="Status"
        :sortable="true"
      >
        <template #body="{ data }">
          <ToggleSwitch
            v-model="data.isActive"
            @change="updateStatus(data)"
          />
        </template>
      </Column>
    </DataTable>
  </Card>
</template>

<script setup>
import { ref } from 'vue'
import PageHeader from '@/components/page/PageHeader.vue'
import Card from '@/components/card/Card.vue'
import Input from '@/components/forms/Input.vue'
import ToggleSwitch from '@/components/forms/ToggleSwitch.vue'
import useApiRequest from '@/composables/useApiRequest'
import { alertToast } from '@/utilities/notification'

import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import { FilterMatchMode } from '@primevue/core/api'

const cronjobs = ref([])
const cronjobsApiRequest = useApiRequest()

const filters = ref(null)

function initFilters() {
  filters.value = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    id: { value: null, matchMode: FilterMatchMode.IN },
    missingInfo: { value: null, matchMode: FilterMatchMode.IN }
  }
}
initFilters()

const clearFilters = () => {
  initFilters()
}

/**
 * Load cron jobs and normalize legacy/new API shapes.
 */
const loadCronjobs = async () => {
  try {
    const response = await cronjobsApiRequest.send({
      endpoint: '/v1/admin/cronjobs'
    })

    const payload = response.data
    cronjobs.value = Array.isArray(payload) ? payload : Array.isArray(payload?.data) ? payload.data : []
  } catch {
    cronjobs.value = []
  }
}

loadCronjobs()

const updateStatus = async job => {
  await useApiRequest()
    .send({
      endpoint: `/v1/admin/cronjobs/${job.id}`,
      method: 'PATCH',
      params: {
        isActive: String(job.isActive)
      }
    })
    .then(response => {
      if (response.success) {
        alertToast('CronJob status updated', null, 'success')
      }
    })
}
</script>
